import { createContext, FC, ReactNode, useContext, useMemo } from "react"
import {
  B2BPaymentMethod,
  Company,
  CorePaymentMethod,
  Maybe,
  PaymentMethodState,
  PaymentMethodPurposeEnumType,
  CompanyCurrencyEnumType,
} from "@oysterhr/schema"
import { MandateSign } from "../sepa/hooks/useMandateSign"
import { useCompanyPaymentMethodsActions } from "../hooks/useCompanyPaymentMethodsActions"
import { ApolloQueryResult } from "@apollo/client"
import { useCompanyPaymentsSettingsContext } from "./CompanyPaymentsSettingsContext"

interface CompanyPaymentMethodsContextType {
  // Data
  companyId: string
  mandateSign?: MandateSign
  selectedPaymentMethod?: CorePaymentMethod | B2BPaymentMethod
  paymentCurrencyCode?: CompanyCurrencyEnumType
  paymentMethod?: Maybe<CorePaymentMethod>
  customerBankAccount?: Maybe<B2BPaymentMethod>
  directDebitSupported?: boolean

  // Modal states
  modals: {
    showDeleteModal: boolean
    setShowDeleteModal: (show: boolean) => void
    showAuthorizeModal: boolean
    setShowAuthorizeModal: (show: boolean) => void
    showConfirmSharingModal: boolean
    setShowConfirmSharingModal: (show: boolean) => void
    showResendMandateModal: boolean
    setShowResendMandateModal: (show: boolean) => void
    showCreditorIdModal: boolean
    setShowCreditorIdModal: (show: boolean) => void
  }

  // Derived data
  ftePaymentMethod?: CorePaymentMethod | B2BPaymentMethod | null
  contractorPaymentMethod?: CorePaymentMethod | null
  wireTransferForFte: boolean
  wireTransferForContractor: boolean

  // Actions
  refetch: () => Promise<ApolloQueryResult<{ company: Company }>>
  onAuthorize: (paymentMethod: CorePaymentMethod | B2BPaymentMethod) => void
  onDelete: (paymentMethod: CorePaymentMethod | B2BPaymentMethod) => void
  onResendMandate: () => void
  openConfirmSharingModal: () => void
  openCreditorIdModal: (paymentMethod: CorePaymentMethod | B2BPaymentMethod) => void
  reconfirmMandateLodgingSuccessCallback: () => Promise<void>
}

export const CompanyPaymentMethodsContext = createContext<
  CompanyPaymentMethodsContextType | undefined
>(undefined)

interface CompanyPaymentMethodsProviderProps {
  children: ReactNode
  companyId: string
  company?: Company
  refetch: () => Promise<
    ApolloQueryResult<{
      company: Company
    }>
  >
}

export const CompanyPaymentMethodsProvider: FC<CompanyPaymentMethodsProviderProps> = ({
  children,
  companyId,
  company,
  refetch,
}) => {
  const {
    onAuthorize,
    openCreditorIdModal,
    openConfirmMandatorySharingModal,
    openDeleteModal,
    openResendMandateModal,
    reconfirmMandateLodgingSuccessCallback,
    mandateSign,
    selectedPaymentMethod,
    modals,
  } = useCompanyPaymentMethodsActions(refetch, company)
  const { contractorDirectDebitEnabled } = useCompanyPaymentsSettingsContext()

  // Company related data
  const paymentMethod = useMemo(() => company?.paymentMethod, [company?.paymentMethod])
  const customerBankAccount = useMemo(
    () => company?.customerBankAccount,
    [company?.customerBankAccount],
  )
  const directDebitSupported = useMemo(
    () => company?.directDebitSupported,
    [company?.directDebitSupported],
  )
  const paymentCurrencyCode = useMemo(
    () => company?.paymentCurrencyCode as CompanyCurrencyEnumType,
    [company?.paymentCurrencyCode],
  )

  // Calculate ftePaymentMethod
  const ftePaymentMethod = useMemo(() => {
    if (
      !!paymentMethod?.purpose &&
      [
        PaymentMethodPurposeEnumType.CollectionAndPassing,
        PaymentMethodPurposeEnumType.Collection,
      ].includes(paymentMethod?.purpose)
    )
      return paymentMethod

    return customerBankAccount
  }, [paymentMethod, customerBankAccount])

  // Calculate contractorPaymentMethod
  const contractorPaymentMethod = useMemo(() => {
    if (
      !!paymentMethod?.purpose &&
      [
        PaymentMethodPurposeEnumType.CollectionAndPassing,
        PaymentMethodPurposeEnumType.Passing,
      ].includes(paymentMethod?.purpose)
    )
      return paymentMethod

    return null
  }, [paymentMethod])

  // Calculate wireTransfer flags
  const wireTransferForContractor = useMemo(
    () =>
      contractorDirectDebitEnabled === false ||
      contractorPaymentMethod?.disabledForContractors === true ||
      contractorPaymentMethod?.state !== PaymentMethodState.AuthorizedForDirectDebit,
    [
      contractorDirectDebitEnabled,
      contractorPaymentMethod?.disabledForContractors,
      contractorPaymentMethod?.state,
    ],
  )

  const wireTransferForFte = useMemo(
    () =>
      ftePaymentMethod?.disabledForFtes === true ||
      ftePaymentMethod?.state !== PaymentMethodState.AuthorizedForDirectDebit,
    [ftePaymentMethod?.disabledForFtes, ftePaymentMethod?.state],
  )

  const contextValue = useMemo<CompanyPaymentMethodsContextType>(
    () => ({
      // Data
      companyId,
      mandateSign,
      selectedPaymentMethod,
      modals,
      // Company data
      paymentMethod,
      customerBankAccount,
      directDebitSupported,
      paymentCurrencyCode,

      // Calculated data
      ftePaymentMethod,
      contractorPaymentMethod,
      wireTransferForFte,
      wireTransferForContractor,

      // Actions
      refetch,
      onAuthorize,
      onDelete: openDeleteModal,
      onResendMandate: openResendMandateModal,
      openConfirmSharingModal: openConfirmMandatorySharingModal,
      openCreditorIdModal,
      reconfirmMandateLodgingSuccessCallback,
    }),
    [
      companyId,
      mandateSign,
      selectedPaymentMethod,
      modals,
      paymentMethod,
      customerBankAccount,
      directDebitSupported,
      paymentCurrencyCode,
      ftePaymentMethod,
      contractorPaymentMethod,
      wireTransferForFte,
      wireTransferForContractor,
      refetch,
      onAuthorize,
      openDeleteModal,
      openResendMandateModal,
      openConfirmMandatorySharingModal,
      openCreditorIdModal,
      reconfirmMandateLodgingSuccessCallback,
    ],
  )

  return (
    <CompanyPaymentMethodsContext.Provider value={contextValue}>
      {children}
    </CompanyPaymentMethodsContext.Provider>
  )
}

export const useCompanyPaymentMethodsContext = (): CompanyPaymentMethodsContextType => {
  const context = useContext(CompanyPaymentMethodsContext)

  if (!context) {
    throw new Error(
      "useCompanyPaymentMethodsContext must be used within a CompanyPaymentMethodsProvider",
    )
  }

  return context
}
